/**
 * Job Files card — pure derivation (Issue #719).
 *
 * Kept out of the component so the labelling, grouping and "what's new since I
 * last looked" rules are unit-testable without a DOM, matching the split
 * already used by steps.ts and call-options.ts.
 */

export type PaperworkKind =
    | "original_work_order"
    | "completed_work_order"
    | "work_order_scan"
    | "official_test_report"
    /** Signed paper form the office converted into the result (#1300). */
    | "paper_test_report"
    | "test_report"
    | "inspection_report"
    | "cheat_sheet"
    | "office_document"
    | "photo";

export type PaperworkSource = "office" | "tech" | "system" | "servicetitan" | "unknown";

export interface JobFile {
    id: string;
    kind: PaperworkKind;
    source: PaperworkSource;
    fileName: string;
    mimeType: string | null;
    fileSize: number;
    createdAt: string;
    uploadedByName: string | null;
    notes: string | null;
    photoTag: string | null;
    assetId: string | null;
    assetSerial: string | null;
    isImage: boolean;
    isPdf: boolean;
    isServiceTitan: boolean;
}

export interface JobPaperworkResponse {
    jobId: string;
    files: JobFile[];
    serviceTitanFiles: JobFile[];
    counts: { total: number; office: number; tech: number; system: number; photos: number; serviceTitan: number };
    hasOriginalWorkOrder: boolean;
    originalWorkOrderMissing: boolean;
}

/** Badge text. Tech vocabulary, not category names. */
export const KIND_LABEL: Record<PaperworkKind, string> = {
    original_work_order: "Original WO",
    completed_work_order: "Completed WO",
    work_order_scan: "Office scan",
    official_test_report: "Official report",
    paper_test_report: "Signed paper",
    test_report: "Test report",
    inspection_report: "Inspection",
    cheat_sheet: "Cheat sheet",
    office_document: "Document",
    photo: "Photo",
};

/** Full-sentence names for the two pinned rows at the top of the card. */
export const HERO_TITLE: Partial<Record<PaperworkKind, string>> = {
    original_work_order: "Original Work Order",
    completed_work_order: "Completed Work Order",
    work_order_scan: "Work Order (office scan)",
};

/**
 * Who put this here, in words a tech reads rather than a role enum. Generated
 * PDFs say so plainly — `uploaded_by` on those rows is whoever triggered the
 * render, so naming a person would be misleading.
 */
export function sourceLabel(file: JobFile): string {
    switch (file.source) {
        case "system":
            return "Generated by Groundwork";
        case "servicetitan":
            return "Imported";
        case "office":
            return file.uploadedByName ? `Office · ${file.uploadedByName}` : "From the office";
        case "tech":
            return file.uploadedByName ? `Tech · ${file.uploadedByName}` : "From the field";
        default:
            return file.uploadedByName ?? "Unknown";
    }
}

/** Short date for a file row. Deliberately no time — the day is what matters. */
export function fileDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** "Office · Darcy W · Jul 28" — the one-line provenance under a filename. */
export function whoAndWhen(file: JobFile): string {
    return [sourceLabel(file), fileDate(file.createdAt)].filter(Boolean).join(" · ");
}

export function formatFileSize(bytes: number): string {
    if (!bytes || bytes < 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type FileChip = "all" | "work_orders" | "reports" | "photos" | "other";

export const CHIP_LABEL: Record<FileChip, string> = {
    all: "All",
    work_orders: "Work orders",
    reports: "Reports",
    photos: "Photos",
    other: "Other",
};

const WORK_ORDER_KINDS: PaperworkKind[] = ["original_work_order", "completed_work_order", "work_order_scan"];
const REPORT_KINDS: PaperworkKind[] = [
    "official_test_report",
    "paper_test_report",
    "test_report",
    "inspection_report",
    "cheat_sheet",
];

export function matchesChip(file: JobFile, chip: FileChip): boolean {
    switch (chip) {
        case "all":
            return true;
        case "work_orders":
            return WORK_ORDER_KINDS.includes(file.kind);
        case "reports":
            return REPORT_KINDS.includes(file.kind);
        case "photos":
            return file.kind === "photo";
        case "other":
            return !WORK_ORDER_KINDS.includes(file.kind) && !REPORT_KINDS.includes(file.kind) && file.kind !== "photo";
    }
}

export function chipCounts(files: JobFile[]): Record<FileChip, number> {
    return {
        all: files.length,
        work_orders: files.filter((f) => matchesChip(f, "work_orders")).length,
        reports: files.filter((f) => matchesChip(f, "reports")).length,
        photos: files.filter((f) => matchesChip(f, "photos")).length,
        other: files.filter((f) => matchesChip(f, "other")).length,
    };
}

/**
 * The two rows pinned above the fold. `rest` keeps everything else in the
 * server's order, so the hero rows are a promotion, not a re-sort.
 */
export function partitionHeroFiles(files: JobFile[]): {
    original: JobFile | null;
    completed: JobFile | null;
    rest: JobFile[];
} {
    const original = files.find((f) => f.kind === "original_work_order") ?? null;
    // The office's scan of the paper WO stands in when no PDF was generated —
    // to a tech, "the completed work order" is whichever one exists.
    const completed =
        files.find((f) => f.kind === "completed_work_order") ?? files.find((f) => f.kind === "work_order_scan") ?? null;
    const heroIds = new Set([original?.id, completed?.id].filter(Boolean) as string[]);
    return { original, completed, rest: files.filter((f) => !heroIds.has(f.id)) };
}

/** localStorage key holding the last time this tech looked at a job's files. */
export function seenStorageKey(jobId: string): string {
    return `gwc.jobFilesSeen.${jobId}`;
}

/**
 * Ids added since the tech last opened this job's files. Purely local and
 * offline-safe: `reviewed_at` on job_attachments belongs to the office's
 * WO-review flow and must not be overloaded for a "new to me" dot.
 */
export function markNewFiles(files: JobFile[], lastSeenIso: string | null): Set<string> {
    if (!lastSeenIso) return new Set();
    const seenAt = new Date(lastSeenIso).getTime();
    if (Number.isNaN(seenAt)) return new Set();
    return new Set(
        files
            .filter((f) => {
                const t = new Date(f.createdAt).getTime();
                return !Number.isNaN(t) && t > seenAt;
            })
            .map((f) => f.id),
    );
}

/** Photos in card order, shaped for the shared touch lightbox. */
export function photoFiles(files: JobFile[]): JobFile[] {
    return files.filter((f) => f.kind === "photo");
}
